import './DetailedRatings.scss'

import {
  ItemRatings,
  InternalComponentAPI,
  InternalPluginProps,
  Rate,
  Translations,
  UpdatedRatings
} from '../../utils/types'
import { addEventListener } from '../../utils/events'
import { withClass, withClassHierarchy } from '../../utils/selectors'
import { formatNumber, formatPercent } from '../../utils/numberFormat'
import StarsDisplay from '../StarsDisplay'
import { setChildContent, setChildStyle } from '../../utils/dom'
import { hasRatings, hasUserRating } from '../../utils/ratings'
import { classlist } from '../../utils/classNames'

const DETAILED_RATINGS_CLASS = 'detailed-ratings'
const HAS_RATINGS_CLASS = 'has-ratings'
const HAS_USER_RATINGS_CLASS = 'has-user-ratings'
const RATE_ITEM_BTN_CLASS = 'detailed-ratings__rate-btn'
const RATE_ITEM_UPDATE_BTN_CLASS = 'detailed-ratings__update-rate-btn'
const CONTENT_SUMMARY_CLASS = 'ratings-summary'
const RATING_DISPLAY_CLASS = 'ratings-summary__stars'
const CONTENT_SUMMARY_AVERAGE_CLASS = 'ratings-summary__average-rating'
const CONTENT_SUMMARY_COUNT_CLASS = 'ratings__ratings-count-value'
const CONTENT_DISTRIBUTION_PERCENTAGE_CLASS = 'ratings-distribution__precentage'
const CONTENT_DISTRIBUTION_BAR_CLASS = 'ratings-distribution__bar'
const FOOTER_CLASS = 'detailed-ratings__footer'
const FOOTER_USER_RATING_VALUE_CLASS = 'detailed-ratings__user-rating-value'
const CONTENT_DISTRIBUTION_ITEM_CLASS = (rate: Rate) =>
  `ratings-distribution__item--${rate}`

const RATES: Rate[] = [5, 4, 3, 2, 1]

type DetailedRatingsProps = InternalPluginProps & {
  target: Element
  onSubmitRatingClick: (event: Event) => void
}

const DetailedRatings = ({
  target,
  onSubmitRatingClick,
  ...props
}: DetailedRatingsProps): InternalComponentAPI => {
  // Build HTML
  target.innerHTML = detailedRatingsContent(props)

  const avgRatingStarsDisplay = StarsDisplay({
    rating: props.itemRatings.averageRating,
    target: target.querySelector(
      withClassHierarchy([CONTENT_SUMMARY_CLASS, RATING_DISPLAY_CLASS])
    )!
  })

  const userRatingStarsDisplay = props.currentUserRating ? StarsDisplay({
    rating: props.currentUserRating?.rating || 5,
    target: target.querySelector(
      withClassHierarchy([FOOTER_CLASS, RATING_DISPLAY_CLASS])
    )!
  }): undefined

  // Event listners
  addEventListener({
    target,
    eventName: 'click',
    selector: withClass(RATE_ITEM_BTN_CLASS),
    handler: onSubmitRatingClick
  })
  addEventListener({
    target,
    eventName: 'click',
    selector: withClass(RATE_ITEM_UPDATE_BTN_CLASS),
    handler: onSubmitRatingClick
  })

  const updateRatings = (updatedRatings: UpdatedRatings) => {
    // Has ratings classes
    target
      .querySelector(withClass(DETAILED_RATINGS_CLASS))
      ?.classList.add(HAS_RATINGS_CLASS, HAS_USER_RATINGS_CLASS)
    // Avg. stars
    avgRatingStarsDisplay.updateRating(updatedRatings.itemRatings.averageRating)
    // Avg. rating
    setChildContent({
      parent: target,
      selector: withClass(CONTENT_SUMMARY_AVERAGE_CLASS),
      html: formatNumber(updatedRatings.itemRatings.averageRating)
    })
    // Ratings count
    setChildContent({
      parent: target,
      selector: withClass(CONTENT_SUMMARY_COUNT_CLASS),
      html: formatNumber(updatedRatings.itemRatings.numberOfRatings)
    })
    // Rating distribution
    RATES.forEach(rate => {
      // Rating N - Bar
      setChildStyle({
        parent: target,
        selector: withClassHierarchy([
          CONTENT_DISTRIBUTION_ITEM_CLASS(rate),
          CONTENT_DISTRIBUTION_BAR_CLASS
        ]),
        property: 'width',
        value: formatPercent(updatedRatings.itemRatings.distribution[rate])
      })
      // Rating N - %
      setChildContent({
        parent: target,
        selector: withClassHierarchy([
          CONTENT_DISTRIBUTION_ITEM_CLASS(rate),
          CONTENT_DISTRIBUTION_PERCENTAGE_CLASS
        ]),
        html: formatPercent(updatedRatings.itemRatings.distribution[rate])
      })
    })
    // User rating - stars
    userRatingStarsDisplay?.updateRating(
      updatedRatings.currentUserRating.rating
    )
    // User rating - value
    setChildContent({
      parent: target,
      selector: withClass(FOOTER_USER_RATING_VALUE_CLASS),
      html: formatNumber(updatedRatings.currentUserRating.rating)
    })
  }

  // Public API
  return {
    updateRatings,
    destroy: () => {
      avgRatingStarsDisplay.destroy()
      userRatingStarsDisplay?.destroy()
      target.innerHTML = ''
    }
  }
}

export default DetailedRatings

//
// Templates
//

const detailedRatingsContent = (props: InternalPluginProps): string => {
  const { translations, itemRatings } = props
  return `
    <div class="${classlist({
      [DETAILED_RATINGS_CLASS]: true,
      [HAS_RATINGS_CLASS]: hasRatings(itemRatings),
      [HAS_USER_RATINGS_CLASS]: hasUserRating(props) && !!props.userName
    })}">
      <h2 class="detailed-ratings__title">${translations.ratings}</h2>
      <div class="detailed-ratings__content">
        <div class="ratings-distribution">
          ${RATES.map(rate => ratingDistributionItem(rate, itemRatings)).join(
            ''
          )}
        </div>
        <div class="${CONTENT_SUMMARY_CLASS}">
          <div class="${CONTENT_SUMMARY_AVERAGE_CLASS}">
          ${formatNumber(itemRatings.averageRating)}
          </div>
          <div class="${RATING_DISPLAY_CLASS}"></div>
          <div class="ratings-summary__ratings-count">
            <span class="${CONTENT_SUMMARY_COUNT_CLASS}">
              ${formatNumber(itemRatings.numberOfRatings)}
            </span>
            <span class="ratings-summary__ratings-count-label">
              ${translations.ratings}
            </span>
          </div>
        </div>
      </div>
      ${(props.userName) ? `
      <div class="${FOOTER_CLASS}">
        <hr class="detailed-ratings__divider">
        ${FooterWithNoRating(translations)}
        ${FooterWithUserRating(props)}
      </div>
      ` : ""}
    </div>
  `
}

const FooterWithNoRating = (translations: Translations) => `
  <div class="detailed-ratings__no-user">
    <button class="${RATE_ITEM_BTN_CLASS}">
      ${translations.rateRecipe}
    </button>
  </div>
`

const FooterWithUserRating = ({
  userName,
  currentUserRating,
  translations
}: InternalPluginProps) => `
  <div class="detailed-ratings__user">
    <div class="detailed-ratings__user-name">${userName ? userName : ""}</div>
    <div class="detailed-ratings__user-rating-section">
      <div class="${FOOTER_USER_RATING_VALUE_CLASS}">
        ${currentUserRating?.rating}
      </div>
      <div class="${RATING_DISPLAY_CLASS}"></div>
      <button class="${RATE_ITEM_UPDATE_BTN_CLASS}">
        ${translations.updateRating}
      </button>
    </div>
  </div>
`

const ratingDistributionItem = (rate: Rate, itemRatings: ItemRatings) => `
  <div class="ratings-distribution__item ${CONTENT_DISTRIBUTION_ITEM_CLASS(
    rate
  )}">
    <span class="ratings-distribution__value">
      ${rate}
    </span>
    <span class="ratings-distribution__star-icon"></span>
    <div class="ratings-distribution__progress-bar">
      <span
        class="${CONTENT_DISTRIBUTION_BAR_CLASS}"
        style="width: ${formatPercent(itemRatings.distribution[rate])}"
      ></span>
    </div>
    <div class="${CONTENT_DISTRIBUTION_PERCENTAGE_CLASS}">
      ${formatPercent(itemRatings.distribution[rate])}
    </div>
  </div>
`
